# Previewing a job ad

The SEEK API provides the ability for a hirer to preview a job ad as it would appear on SEEK.

By integrating job ad preview into your posting flow, hirers will be able to:

- Preview how their job ad will display in the SEEK search results and job details page.

- See how their job ad will look on desktop and mobile devices.

- Verify that their company [logo and cover image] is as expected.

- Spot issues with their job ad prior to posting, reducing the need to update their job ad later.

A job ad preview can be programmatically retrieved by your software when [posting] or [updating] a job ad.
This allows you to build your own button, write your own query to the SEEK API and choose how it is handled.

import { Actions, ButtonLink, IconNewWindow } from 'braid-design-system';

<Actions>
  <ButtonLink
    href="https://job-ad-preview.seek.com/anzPublicTest?id=seekAnzPublicTest:postedPositionProfilePreview:seekApi:37EWfWxhwVEGTT5BcjfE2L"
    icon={<IconNewWindow />}
    size="small"
    target="_blank"
    tone="brandAccent"
  >
    Try it out
  </ButtonLink>
</Actions>

## Browser support

The Job Ad Preview page tracks our standard [browser support policy].

[browser support policy]: /introduction/browser-support

## Before you begin

- SEEK recommends using a [browser token] to generate a job ad preview directly from a hirer’s browser.
  For the browser token to work, you will need to include the `query:posted-position-profile-previews` scope in your request.

- You can also generate a job ad preview URL for a hirer from your backend using a [partner token].

## Building job ad preview

Your implementation will need to:

1. Display a button for hirers to click on to preview the job ad.

   The button should only be enabled if the hirer has provided a `positionTitle` and a `seekAnzWorkTypeCode`.

1. Execute the [`postedPositionProfilePreview`] query to generate a job ad preview when the button is clicked.

   Your software must provide the [state of the job ad] to the [`positionProfile` argument].
   It should match what you eventually post for an accurate preview.

   When rendering the job ad preview during a job ad update workflow, include the `profileId` obtained from the [`PositionProfilePosted` event] for the ad as part of the [`postedPositionProfilePreview`] query.
   For additional information, refer to [`PostedPositionProfilePreview_PositionProfileInput`].

1. With the [`previewUri` field] from the query response, open a new tab for the hirer or render an `<iframe />` in flow.

   The URI is valid for 24 hours.

[`positionprofile` argument]: /schema/query/postedPositionProfilePreview#/argument/positionProfile
[`postedpositionprofilepreview`]: /schema/query/postedPositionProfilePreview
[`previewuri` field]: /schema/named-type/PostedPositionProfilePreview#/field/previewUri
[state of the job ad]: /schema/named-type/PostedPositionProfilePreview_PositionProfileInput
[`PositionProfilePosted` event]: /use-cases/job-posting/managing-job-ads/posting-a-job-ad#positionprofileposted
[`PostedPositionProfilePreview_PositionProfileInput`]: /schema/named-type/PostedPositionProfilePreview_PositionProfileInput

### Example query

```graphql
query ($positionProfile: PostedPositionProfilePreview_PositionProfileInput!) {
  postedPositionProfilePreview(positionProfile: $positionProfile) {
    previewUri {
      url
    }
  }
}
```

```json
{
  "positionProfile": {
    "positionTitle": "Senior Developer",
    "positionOrganizations": "seekAnzPublicTest:organization:seek:93WyyF1h",
    "jobCategories": "seekAnzPublicTest:jobCategory:seek:2EFstqFvP",
    "positionLocation": "seekAnzPublicTest:location:seek:2FqwWaaMV",
    "offeredRemunerationPackage": {
      "descriptions": ["Base + Super + Profit Share"],
      "basisCode": "Hourly",
      "ranges": [
        {
          "intervalCode": "Hour",
          "maximumAmount": {
            "currency": "AUD",
            "value": 200
          },
          "minimumAmount": {
            "currency": "AUD",
            "value": 100
          }
        }
      ]
    },
    "postingInstructions": [
      {
        "seekAdvertisementProductId": "globalPublicTest:adProduct:seekApi:EWHKTY9D6mJFiA5EH6P7MFbByJ1JVteAQQkfa3uMJLPJ",
        "brandingId": "globalPublicTest:advertisementBranding:hirerBranding:5DU2L2vQsyTXuu9NEUKT3f"
      }
    ],
    "positionFormattedDescriptions": [
      {
        "descriptionId": "AdvertisementDetails",
        "content": "My job ad details"
      },
      {
        "descriptionId": "SearchSummary",
        "content": "Search summary content"
      },
      {
        "descriptionId": "SearchBulletPoint",
        "content": "Search bullet point 1"
      },
      {
        "descriptionId": "SearchBulletPoint",
        "content": "Search bullet point 2"
      },
      {
        "descriptionId": "SearchBulletPoint",
        "content": "Search bullet point 3"
      }
    ],
    "seekAnzWorkTypeCode": "FullTime",
    "seekWorkArrangementCodes": ["Remote"],
    "seekApplicationQuestionnaireId": "seekAnzPublicTest:applicationQuestionnaire:rrv2:SqFmkV8S2dMipyqbHjD9Mr",
    "seekVideo": {
      "url": "https://www.youtube.com/watch?v=vsLYc3gzMzw",
      "seekAnzPositionCode": "Below"
    }
  }
}
```

```json lovato-ignore-runnable-graphql-operation
{
  "postedPositionProfilePreview": {
    "previewUri": {
      "url": "https://job-ad-preview.seek.com/anzPublicTest?id=seekAnzPublicTest:postedPositionProfilePreview:seekApi:37EWfWxhwVEGTT5BcjfE2L"
    }
  }
}
```

[browser token]: /auth/browser-tokens
[partner token]: /auth/partner-tokens
[posting]: /use-cases/job-posting/managing-job-ads/posting-a-job-ad
[updating]: /use-cases/job-posting/managing-job-ads/updating-a-job-ad
[logo and cover image]: /use-cases/job-posting/branding
